import { useEffect, useRef, useState } from "react";

const MAX_SLOPE = Math.tan((9 * Math.PI) / 180);
const HARD = '[data-testid="hero-eyebrow"], [data-testid="hero-plan-event-btn"], [data-testid="hero-explore-btn"], [data-testid="hero-review-badge"], .hero-polaroid, img';
const SOFT = '[data-testid="hero-intro"]';
const GAP_MS = 0;
const PAUSE_MS = 11000;
const GOLD_MS = 5000;
const SPEED = 850; // px/s for the rising Star C
const CROSS_SPEED = 2400; // px/s for the horizontal Stars A/B
const durFor = (dist, speed = SPEED) => `${(dist / speed).toFixed(3)}s`;

const segHitsRect = (x0, y0, x1, y1, r, pad = 8) => {
  const L = r.left - pad, R = r.right + pad, T = r.top - pad, B = r.bottom + pad;
  for (let i = 0; i <= 80; i++) { const x = x0 + ((x1 - x0) * i) / 80, y = y0 + ((y1 - y0) * i) / 80; if (x > L && x < R && y > T && y < B) return true; }
  return false;
};
const rel = (el, hb) => { const r = el.getBoundingClientRect(); return { left: r.left - hb.left, right: r.right - hb.left, top: r.top - hb.top, bottom: r.bottom - hb.top, width: r.width }; };

// Returns CSS vars for a star, or null if no clear lane exists. Head position = sx + trail.
const plan = (hero, mode) => {
  const eyebrow = hero.querySelector('[data-testid="hero-eyebrow"]');
  const hb = hero.getBoundingClientRect();
  const w = hero.clientWidth, H = hero.clientHeight, mobile = w < 640;
  const trail = mobile ? 200 : 340, head = mobile ? 9 : 15;
  if (mode === "rise") {
    const word = hero.querySelector('[data-testid="hero-gold-word"]');
    if (!word) return null;
    const t = rel(word, hb);
    const tx = t.left + t.width / 2, ty = (t.top + t.bottom) / 2;
    const hard = [...hero.querySelectorAll(HARD)].map((el) => rel(el, hb)).filter((r) => r.width > 0);
    const soft = [...hero.querySelectorAll(SOFT)].map((el) => rel(el, hb));
    const y0 = H + head, rise = y0 - ty, rt = Math.round(trail * 0.85);
    const mk = (hx0) => ({ "--star-y": `${y0}px`, "--trail": `${rt}px`, "--head": `${head}px`, "--sx": `${hx0 - rt}px`, "--ex": `${tx - rt}px`, "--star-dy": `${-rise}px`, "--star-rot": `${(Math.atan2(-rise, tx - hx0) * 180) / Math.PI}deg`, "--star-dur": durFor(Math.hypot(rise, tx - hx0)), target: { x: tx, y: ty } });
    // Prefer steep lanes (start near the word's x), widening outward until the lane clears the hard blockers.
    const starts = [];
    for (let dx = 40; dx <= w; dx += 24) { if (tx + dx < w - 8) starts.push(tx + dx); if (tx - dx > 8) starts.push(tx - dx); }
    const ok = (hx0, list) => !list.some((r) => segHitsRect(hx0, y0, tx, ty, r));
    const clean = starts.find((hx0) => ok(hx0, hard) && ok(hx0, soft));
    if (clean !== undefined) return mk(clean);
    const soft1 = starts.find((hx0) => ok(hx0, hard));
    return soft1 !== undefined ? mk(soft1) : null;
  }
  const band = eyebrow ? eyebrow.getBoundingClientRect().top - hb.top : 0;
  if (band < 40) return null;
  const travel = w + trail * 2 + 80;
  const y0 = 6 + Math.random() * Math.max(0, band * 0.4 - 6);
  const dy = Math.min(MAX_SLOPE * travel, Math.max(8, band - y0 - 22));
  const rtl = mode === "rtl";
  const theta = (Math.atan2(dy, travel) * 180) / Math.PI;
  return { "--star-y": `${y0}px`, "--trail": `${trail}px`, "--head": `${head}px`, "--sx": rtl ? `${w + trail}px` : `${-trail - 60}px`, "--ex": rtl ? `${-trail - 80}px` : `${w + trail + 20}px`, "--star-dy": `${dy}px`, "--star-rot": `${rtl ? 180 - theta : theta}deg`, "--star-dur": durFor(Math.hypot(travel, dy), CROSS_SPEED) };
};

const typingDoneMs = (hero) => {
  const chars = parseInt(getComputedStyle(hero.querySelector(".type-text") || hero).getPropertyValue("--chars")) || 0;
  return 400 + chars * 62 + 350;
};

const SEQUENCE = ["ltr", "rtl", "rise"];

export const HeroStars = () => {
  const hostRef = useRef(null);
  const [active, setActive] = useState(null); // { mode, vars, key }
  const [spark, setSpark] = useState(null);
  const timer = useRef(null);
  const goldTimer = useRef(null);
  const keyRef = useRef(0);

  const fire = (i) => {
    const hero = hostRef.current?.parentElement;
    if (!hero) return;
    const mode = SEQUENCE[i];
    const vars = plan(hero, mode);
    if (!vars) { advance(i); return; }
    keyRef.current += 1;
    setActive({ mode, vars, key: keyRef.current });
  };
  const advance = (i) => {
    setActive(null);
    const next = i + 1;
    timer.current = setTimeout(() => fire(next % SEQUENCE.length), next < SEQUENCE.length ? GAP_MS : PAUSE_MS);
  };
  const impact = (target) => {
    const word = hostRef.current?.parentElement?.querySelector('[data-testid="hero-gold-word"]');
    setSpark({ ...target, key: keyRef.current });
    if (word) {
      word.classList.add("is-gold");
      clearTimeout(goldTimer.current);
      goldTimer.current = setTimeout(() => word.classList.remove("is-gold"), GOLD_MS);
    }
  };

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const hero = hostRef.current?.parentElement;
    const elapsed = performance.now();
    timer.current = setTimeout(() => fire(0), Math.max(600, typingDoneMs(hero) - elapsed));
    return () => { clearTimeout(timer.current); clearTimeout(goldTimer.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onEnd = (e) => {
    if (e.animationName !== "hero-star-fly" && e.animationName !== "hero-star-rise") return;
    if (active.mode === "rise") impact(active.vars.target);
    advance(SEQUENCE.indexOf(active.mode));
  };

  return (
    <div ref={hostRef} aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-[4] h-full overflow-hidden">
      {active && (
        <div key={active.key} data-testid={`hero-shooting-star-${active.mode}`} className={`hero-star${active.mode === "rise" ? " hero-star--rise" : ""}`} style={Object.fromEntries(Object.entries(active.vars).filter(([k]) => k.startsWith("--")))} onAnimationEnd={onEnd}>
          <i className="hero-star-trail2" />
          <span className="hero-star-head" />
        </div>
      )}
      {spark && <span key={spark.key} data-testid="hero-gold-spark" className="hero-spark" style={{ left: spark.x, top: spark.y }} onAnimationEnd={() => setSpark(null)} />}
    </div>
  );
};
